import { useRef, useState } from 'react'
import { ShieldCheck, Mail, Phone, Building2, Globe2, BadgeCheck, CalendarClock, Activity, Pencil, LockKeyhole, Laptop, Smartphone, UploadCloud, X } from 'lucide-react'
import { Modal } from '../common/Modal'

const sessions = [
  { device: 'MacBook Pro · Chrome', location: 'Mumbai, IN', time: 'Active now', icon: <Laptop size={14} /> },
  { device: 'iPhone 15 · Safari',   location: 'Mumbai, IN', time: '2 hours ago', icon: <Smartphone size={14} /> },
  { device: 'Windows PC · Edge',    location: 'Pune, IN',   time: 'Yesterday',   icon: <Laptop size={14} /> },
]

export default function AdminProfile() {
  const [editOpen, setEditOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [profile, setProfile] = useState({
    name: 'Admin User',
    email: 'admin@example.com',
    phone: '+1 (555) 247-1890',
    team: 'Global Operations',
    country: 'United States',
    role: 'Super Admin · Platform Owner',
    avatarPreview: '',
    avatarName: '',
  })
  const [form, setForm] = useState(profile)
  const fileInputRef = useRef(null)

  const openEdit = () => {
    setForm(profile)
    setEditOpen(true)
  }

  const saveProfile = () => {
    setProfile(form)
    setEditOpen(false)
  }

  const handleAvatarUpload = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    const preview = URL.createObjectURL(file)
    setForm((prev) => ({ ...prev, avatarPreview: preview, avatarName: file.name }))
  }

  const clearAvatar = (e) => {
    e.stopPropagation()
    setForm((prev) => ({ ...prev, avatarPreview: '', avatarName: '' }))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="dash-page page-in">
      <h1 className="page-title">Admin Profile</h1>

      <section className="card profile-hero">
        <div className="profile-hero-left">
          <div className="profile-avatar">
            {profile.avatarPreview ? <img src={profile.avatarPreview} alt="Admin avatar" /> : 'AD'}
          </div>
          <div>
            <h2>{profile.name}</h2>
            <p>{profile.role}</p>
            <div className="profile-tags">
              <span className="badge badge-active"><BadgeCheck size={12} /> Verified</span>
              <span className="badge badge-gold"><CalendarClock size={12} /> Joined Jan 2023</span>
              <span className="badge badge-info"><Activity size={12} /> Last active 12 min ago</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          <button className="btn btn-outline" onClick={() => setPasswordOpen(true)}>
            <LockKeyhole size={14} /> Change Password
          </button>
          <button className="btn btn-primary" onClick={openEdit}>
            <Pencil size={14} /> Edit Profile
          </button>
        </div>
      </section>

      <div className="profile-content">
        <div className="profile-main">
          <section className="card profile-card">
            <div className="card-header">
              <div className="card-title">Personal Information</div>
              <div className="card-subtitle">Contact details and organizational info</div>
            </div>
            <div className="profile-details-grid">
              <div className="detail-item">
                <Mail size={16} />
                <div>
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{profile.email}</span>
                </div>
              </div>
              <div className="detail-item">
                <Phone size={16} />
                <div>
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{profile.phone}</span>
                </div>
              </div>
              <div className="detail-item">
                <Building2 size={16} />
                <div>
                  <span className="detail-label">Department</span>
                  <span className="detail-value">{profile.team}</span>
                </div>
              </div>
              <div className="detail-item">
                <Globe2 size={16} />
                <div>
                  <span className="detail-label">Location</span>
                  <span className="detail-value">{profile.country}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <div className="card-title">Account Security</div>
              <ShieldCheck size={18} style={{ color: 'var(--color-green)' }} />
            </div>
            <div className="security-grid">
              <div className="security-item">
                <div className="security-icon"><ShieldCheck size={20} /></div>
                <div>
                  <span className="security-label">Two-Factor Auth</span>
                  <span className="security-status ok">Enabled</span>
                </div>
              </div>
              <div className="security-item">
                <div className="security-icon"><LockKeyhole size={20} /></div>
                <div>
                  <span className="security-label">Password Strength</span>
                  <span className="security-status ok">Strong</span>
                </div>
              </div>
              <div className="security-item">
                <div className="security-icon"><Mail size={20} /></div>
                <div>
                  <span className="security-label">Recovery Email</span>
                  <span className="security-status ok">Verified</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="profile-sidebar">
          <section className="card">
            <div className="card-header">
              <div className="card-title">Recent Activity</div>
            </div>
            <div className="activity-list">
              <div className="activity-item">
                <div className="activity-icon"><Activity size={16} /></div>
                <div>
                  <p className="activity-text">Logged in from Mumbai, IN</p>
                  <span className="activity-time">2 hours ago</span>
                </div>
              </div>
              <div className="activity-item">
                <div className="activity-icon"><Pencil size={16} /></div>
                <div>
                  <p className="activity-text">Updated profile information</p>
                  <span className="activity-time">1 day ago</span>
                </div>
              </div>
              <div className="activity-item">
                <div className="activity-icon"><ShieldCheck size={16} /></div>
                <div>
                  <p className="activity-text">Enabled 2FA authentication</p>
                  <span className="activity-time">3 days ago</span>
                </div>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <div className="card-title">Active Sessions</div>
              <button className="btn btn-outline btn-sm">Sign out all</button>
            </div>
            <div className="sessions-list">
              {sessions.map((item) => (
                <div key={item.device} className="session-item">
                  <div className="session-icon">{item.icon}</div>
                  <div className="session-info">
                    <span className="session-device">{item.device}</span>
                    <span className="session-location">{item.location}</span>
                    <span className="session-time">{item.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* ── EDIT PROFILE MODAL ── */}
      <Modal
        open={editOpen}
        title="Edit Profile"
        subtitle="Update profile and organization details"
        onClose={() => setEditOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setEditOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveProfile}>Save Changes</button>
          </>
        )}
      >
        <div className="form-grid cols-2">

          {/* ── NEW IMAGE UPLOAD FIELD ── */}
          <div className="form-field full">
            <label>Profile Photo</label>
            <div
              className={`avatar-upload-zone ${form.avatarPreview ? 'has-preview' : ''}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarUpload}
              />

              {form.avatarPreview ? (
                /* ── preview state ── */
                <>
                  <div className="auz-preview-img">
                    <img src={form.avatarPreview} alt="Preview" />
                  </div>
                  <div className="auz-preview-info">
                    <span className="auz-fname">{form.avatarName}</span>
                    <span className="auz-change">Click to change photo</span>
                  </div>
                  <button
                    className="auz-clear"
                    onClick={clearAvatar}
                    title="Remove photo"
                  >
                    <X size={12} />
                  </button>
                </>
              ) : (
                /* ── empty state ── */
                <>
                  <div className="auz-icon">
                    <UploadCloud size={22} />
                  </div>
                  <div className="auz-text">
                    <span className="auz-primary">Click to upload photo</span>
                    <span className="auz-hint">PNG, JPG or GIF — max 5 MB</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="form-field">
            <label>Full Name</label>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter full name" />
          </div>
          <div className="form-field">
            <label>Email Address</label>
            <input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Enter email" />
          </div>
          <div className="form-field">
            <label>Phone Number</label>
            <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Enter phone number" />
          </div>
          <div className="form-field">
            <label>Department</label>
            <input className="input" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} placeholder="Team / Department" />
          </div>
          <div className="form-field full">
            <label>Country</label>
            <input className="input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Enter country" />
          </div>
        </div>
      </Modal>

      {/* ── CHANGE PASSWORD MODAL ── */}
      <Modal
        open={passwordOpen}
        title="Change Password"
        subtitle="Secure your account with a new password"
        onClose={() => setPasswordOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setPasswordOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => setPasswordOpen(false)}>Update Password</button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-field">
            <label>Current Password</label>
            <input className="input" type="password" placeholder="Enter current password" />
          </div>
          <div className="form-field">
            <label>New Password</label>
            <input className="input" type="password" placeholder="Enter new password" />
          </div>
          <div className="form-field">
            <label>Confirm New Password</label>
            <input className="input" type="password" placeholder="Confirm new password" />
          </div>
        </div>
      </Modal>

      <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }
        .profile-hero {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--space-md);
          padding: clamp(1rem, 2vw, 1.3rem);
          background: linear-gradient(135deg, rgba(79,70,229,.2), rgba(6,182,212,.12));
          border: 1px solid var(--border-active);
          border-radius: var(--radius-lg);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          overflow: hidden;
        }
        .profile-hero-left {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          min-width: 0;
        }
        .profile-hero h2 { color: var(--text1); font-size: var(--fs-2xl); letter-spacing: -.02em; }
        .profile-hero p { color: var(--text2); font-size: var(--fs-sm); margin-top: 2px; }
        .profile-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
        .profile-card { display: flex; flex-direction: column; gap: var(--space-md); }
        .profile-avatar {
          width: 52px; height: 52px;
          border-radius: var(--radius-full);
          display: grid; place-items: center;
          font-size: var(--fs-md); font-weight: 700; color: #fff;
          background: linear-gradient(135deg, var(--accent), var(--accent-2));
          overflow: hidden;
        }
        .profile-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .profile-content {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: var(--space-lg);
        }
        .profile-main { display: flex; flex-direction: column; gap: var(--space-lg); }
        .profile-sidebar { display: flex; flex-direction: column; gap: var(--space-lg); }
        .profile-details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-lg);
        }
        .detail-item {
          display: flex; align-items: center; gap: var(--space-md);
          padding: var(--space-md);
          background: var(--bg-raised);
          border-radius: var(--radius-md);
          border: 1px solid var(--border);
        }
        .detail-item svg { color: var(--accent); flex-shrink: 0; }
        .detail-label {
          font-size: var(--fs-xs); color: var(--text3);
          text-transform: uppercase; letter-spacing: 0.5px;
          display: block; margin-bottom: 2px;
        }
        .detail-value { font-size: var(--fs-sm); color: var(--text1); font-weight: 500; }
        .security-grid { display: grid; gap: var(--space-md); }
        .security-item {
          display: flex; align-items: center; gap: var(--space-md);
          padding: var(--space-md);
          background: var(--bg-raised);
          border-radius: var(--radius-md);
          border: 1px solid var(--border);
        }
        .security-icon {
          width: 40px; height: 40px;
          border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center;
          background: rgba(16,185,129,0.1);
          color: var(--color-green);
          border: 1px solid rgba(16,185,129,0.2);
        }
        .security-label { font-size: var(--fs-sm); color: var(--text2); display: block; margin-bottom: 2px; }
        .security-status.ok { color: var(--color-green); font-weight: 600; font-size: var(--fs-xs); }
        .activity-list { display: flex; flex-direction: column; gap: var(--space-md); }
        .activity-item { display: flex; align-items: flex-start; gap: var(--space-md); padding: var(--space-sm) 0; }
        .activity-icon {
          width: 32px; height: 32px;
          border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center;
          background: var(--bg-raised); color: var(--accent);
          border: 1px solid var(--border); flex-shrink: 0;
        }
        .activity-text { font-size: var(--fs-sm); color: var(--text2); margin-bottom: 2px; }
        .activity-time { font-size: var(--fs-xs); color: var(--text4); }
        .sessions-list { display: flex; flex-direction: column; gap: var(--space-md); }
        .session-item {
          display: flex; align-items: center; gap: var(--space-md);
          padding: var(--space-md);
          background: var(--bg-raised);
          border-radius: var(--radius-md);
          border: 1px solid var(--border);
        }
        .session-icon {
          width: 36px; height: 36px;
          border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center;
          background: rgba(79,70,229,0.1); color: var(--accent);
          border: 1px solid rgba(79,70,229,0.2);
        }
        .session-info { flex: 1; }
        .session-device { font-size: var(--fs-sm); color: var(--text1); font-weight: 500; display: block; margin-bottom: 2px; }
        .session-location, .session-time { font-size: var(--fs-xs); color: var(--text3); }

        /* ── AVATAR UPLOAD ZONE ── */
        .avatar-upload-zone {
          position: relative;
          border: 2px dashed var(--border-active);
          border-radius: var(--radius-md);
          padding: 20px 16px;
          display: flex;
          align-items: center;
          gap: 14px;
          cursor: pointer;
          background: var(--bg-raised);
          transition: border-color 0.2s, background 0.2s;
          overflow: hidden;
        }
        .avatar-upload-zone:hover {
          border-color: var(--accent);
          background: var(--bg-active);
        }
        .avatar-upload-zone.has-preview {
          border-style: solid;
          border-color: rgba(16,185,129,0.5);
          background: rgba(16,185,129,0.05);
        }
        .avatar-upload-zone.has-preview:hover {
          border-color: var(--color-green);
          background: rgba(16,185,129,0.08);
        }

        /* icon circle (empty state) */
        .auz-icon {
          width: 48px; height: 48px;
          border-radius: 50%;
          background: var(--accent-bg);
          border: 1px solid var(--border-active);
          display: flex; align-items: center; justify-content: center;
          color: var(--accent);
          flex-shrink: 0;
          transition: transform 0.2s;
        }
        .avatar-upload-zone:hover .auz-icon { transform: scale(1.08); }

        /* text (empty state) */
        .auz-text { display: flex; flex-direction: column; gap: 3px; }
        .auz-primary { font-size: var(--fs-sm); font-weight: 600; color: var(--text1); }
        .auz-hint    { font-size: var(--fs-xs); color: var(--text3); }

        /* preview state */
        .auz-preview-img {
          width: 48px; height: 48px;
          border-radius: 50%;
          overflow: hidden;
          border: 2px solid var(--color-green);
          flex-shrink: 0;
        }
        .auz-preview-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .auz-preview-info { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
        .auz-fname  {
          font-size: var(--fs-sm); font-weight: 600; color: var(--color-green);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .auz-change { font-size: var(--fs-xs); color: var(--text3); }

        /* clear button */
        .auz-clear {
          position: absolute; top: 8px; right: 8px;
          width: 22px; height: 22px;
          border-radius: 50%;
          border: 1px solid var(--border);
          background: var(--bg-surface);
          color: var(--text3);
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
        }
        .auz-clear:hover { color: var(--color-red); border-color: rgba(239,68,68,0.4); background: rgba(239,68,68,0.08); }

        @media (max-width: 1024px) {
          .profile-content { grid-template-columns: 1fr; }
          .profile-hero { flex-direction: column; gap: var(--space-lg); text-align: center; }
          .profile-hero-left { justify-content: center; }
        }
        @media (max-width: 768px) {
          .profile-details-grid { grid-template-columns: 1fr; }
          .profile-hero { padding: var(--space-md); gap: var(--space-md); }
          .profile-hero-left { flex-direction: column; gap: var(--space-sm); }
          .profile-avatar { width: 48px; height: 48px; }
          .profile-hero h2 { font-size: var(--fs-xl); }
          .profile-tags { justify-content: center; margin-top: 8px; }
        }
      `}</style>
    </div>
  )
}